/**
 * KAN-718: раздел "Почтовый ящик": журнал писем организации, новые сверху.
 * Вкладки Входящие / Отправленные / Все, поиск, постраничный вывод; клик по
 * строке открывает письмо, ссылка на клиента открывает его переписку
 * (?dialog=<clientId>). В шапке виден ящик, с которым идёт переписка.
 * KAN-724: вкладка "Не разобрано (N)": письма без привязки к клиенту.
 */
import { useEffect, useState } from 'react';
import { Mail, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PaginationBar } from '@/components/common/PaginationBar';
import { usePageSize } from '@/hooks/usePageSize';
import { PageLayout } from '@/components/layout/PageLayout';
import { EmailsTable } from '@/components/emails/EmailsTable';
import { EmailDetailDialog } from '@/components/emails/EmailDetailDialog';
import { UnmatchedEmailsPanel } from '@/components/emails/UnmatchedEmailsPanel';
import { useDebounce } from '@/hooks/useDebounce';
import { useDialogParam } from '@/hooks/useDialogParam';
import { useEmails } from '@/hooks/useEmails';
import { useEmailMailbox } from '@/hooks/useEmailMailbox';
import { useCanCallApi } from '@/hooks/useApiAccess';
import { useUnmatchedEmailCount } from '@/hooks/useUnmatchedEmails';
import type { EmailDirection, EmailItem, UnmatchedEmailAssignResult } from '@/types/emailMailbox';


type DirectionFilter = 'all' | EmailDirection;
type EmailsTab = DirectionFilter | 'unmatched';

const DIRECTION_TABS: Array<{ value: DirectionFilter; label: string }> = [
  { value: 'in', label: 'Входящие' },
  { value: 'out', label: 'Отправленные' },
  { value: 'all', label: 'Все' },
];

const UNMATCHED_TAB = 'unmatched';

const isEmailsTab = (value: string): value is EmailsTab =>
  value === UNMATCHED_TAB || DIRECTION_TABS.some((t) => t.value === value);

export function EmailsPage(): JSX.Element {
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState<EmailsTab>('all');
  const direction: DirectionFilter = tab === UNMATCHED_TAB ? 'all' : tab;
  const unmatchedCount = useUnmatchedEmailCount();
  const { page, setPage, pageSize, setPageSize } = usePageSize('emails');
  const [opened, setOpened] = useState<EmailItem | null>(null);
  const debouncedSearch = useDebounce(search.trim(), 300);
  const { openDialog } = useDialogParam();
  // KAN-811: mailbox settings are closed to the MANAGER role: no address line.
  const canReadMailbox = useCanCallApi('GET', '/api/v1/email-mailbox/');
  const { data: mailbox } = useEmailMailbox(canReadMailbox);
  const mailboxAddress = mailbox ? mailbox.address || mailbox.platform_address : '';

  useEffect(() => {
    document.title = 'Почтовый ящик - SMETA AI';
  }, []);

  // Новый фильтр или поиск: с первой страницы.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, direction, setPage]);

  const showJournal = tab !== UNMATCHED_TAB;
  const { data, isLoading, isError } = useEmails({
    search: debouncedSearch || undefined,
    direction: direction === 'all' ? undefined : direction,
    page,
    page_size: pageSize,
  });
  const emails = data?.results ?? [];

  const openClient = (email: EmailItem): void => {
    setOpened(null);
    openDialog({
      clientId: email.client_id,
      estimateId: email.estimate_id,
      hint: email.client_name ? { title: email.client_name } : undefined,
    });
  };

  const openAssigned = (result: UnmatchedEmailAssignResult): void => {
    openDialog({
      clientId: result.client_id,
      estimateId: result.estimate_id,
      hint: result.client_name ? { title: result.client_name } : undefined,
    });
  };

  return (
    <PageLayout
      data-testid="emails-page"
      title="Почтовый ящик"
      subtitle={<span className="hidden sm:inline">Письма клиентам и ответы от них</span>}
      titleIcon={
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-blue-600">
          <Mail className="h-5 w-5 text-white" />
        </div>
      }
      breadcrumbs={[{ label: 'Главная', href: '/' }, { label: 'Почтовый ящик' }]}
    >
      {mailboxAddress && (
        <p className="mb-3 text-sm text-gray-600" data-testid="emails-mailbox">
          Ящик: <span className="font-medium text-gray-900">{mailboxAddress}</span>
        </p>
      )}
      <Tabs
        value={tab}
        onValueChange={(value) => {
          if (isEmailsTab(value)) setTab(value);
        }}
        className="mb-4">
        <TabsList aria-label="Направление">
          {DIRECTION_TABS.map((t) => (
            <TabsTrigger key={t.value} value={t.value} data-testid={`emails-tab-${t.value}`}>
              {t.label}
            </TabsTrigger>
          ))}
          <TabsTrigger value={UNMATCHED_TAB} data-testid="emails-tab-unmatched">
            Не разобрано ({unmatchedCount})
          </TabsTrigger>
        </TabsList>
      </Tabs>
      {!showJournal && <UnmatchedEmailsPanel onAssigned={openAssigned} />}
      {showJournal && (
        <>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative sm:w-80">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            aria-label="Поиск писем"
            data-testid="emails-search"
            className="pl-9"
            placeholder="Тема, адрес, клиент"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {isLoading && <p className="text-sm text-gray-500" data-testid="emails-loading">Загрузка...</p>}
      {isError && (
        <p className="text-sm text-red-700" data-testid="emails-error">
          Не удалось загрузить письма
        </p>
      )}
      {!isLoading && !isError && emails.length === 0 && (
        <p className="text-sm text-gray-500" data-testid="emails-empty">
          Писем нет
        </p>
      )}
      {emails.length > 0 && (
        <>
          <EmailsTable emails={emails} onOpenEmail={setOpened} onOpenClient={openClient} />
            <PaginationBar
              page={page}
              pageSize={pageSize}
              count={data?.count ?? 0}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
        </>
      )}

        </>
      )}

      <EmailDetailDialog email={opened} onClose={() => setOpened(null)} onOpenClient={openClient} />
    </PageLayout>
  );
}
